import './init-veaury';
import '../css/app.css';

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { vGlossary } from './directives/glossaryHighlight';



const appName = import.meta.env.VITE_APP_NAME || 'Portal';

const initAhrefsAnalytics = () => {
    if (typeof window === 'undefined' || document.querySelector('script[src="https://analytics.ahrefs.com/analytics.js"]')) {
        return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://analytics.ahrefs.com/analytics.js';
    script.dataset.key = 'gXWbqASZWjqjEMM74mz6gw';
    document.head.appendChild(script);
};

window.__initAnalyticsTools = () => {
    initGoogleAnalytics();
    initAhrefsAnalytics();
    window.dispatchEvent(new CustomEvent('sw:analytics-consent'));
};

const initGoogleAnalytics = () => {
    const measurementId = document.querySelector('meta[name="ga-measurement-id"]')?.getAttribute('content');

    if (!measurementId || typeof window === 'undefined' || typeof document === 'undefined' || typeof window.gtag === 'function') {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', measurementId);
    window.SW_GA_MEASUREMENT_ID = measurementId;

    // Also configure Google Ads conversion tracking if present
    const googleAdsId = document.querySelector('meta[name="google-ads-id"]')?.getAttribute('content');
    if (googleAdsId) {
        window.gtag('config', googleAdsId);
        window.SW_GOOGLE_ADS_ID = googleAdsId;
    }
    const googleAdsConversionLabel = document.querySelector('meta[name="google-ads-conversion-label"]')?.getAttribute('content');
    if (googleAdsConversionLabel) {
        window.SW_GOOGLE_ADS_CONVERSION_LABEL = googleAdsConversionLabel;
    }

    if (document.querySelector(`script[src="https://www.googletagmanager.com/gtag/js?id=${measurementId}"]`)) {
        return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
};

createInertiaApp({
    title: (title) => title,
    resolve: (name) => {
        // Pages root is empty; all generic pages live at Pages/default/.
        // Controllers still call Inertia::render('Welcome') without the
        // prefix, so try bare path first (works for variant paths like
        // 'v01_trustbank/Home' or 'heizung-solar-bremen/Home'), then
        // fall back to default/{name}. DO NOT REMOVE - removing this
        // drops every non-variant domain into 500 because the legacy
        // root files were deleted in commit b0b204cca.
        const pages = import.meta.glob('./Pages/**/*.vue');
        if (pages[`./Pages/${name}.vue`]) return resolvePageComponent(`./Pages/${name}.vue`, pages);
        if (pages[`./Pages/default/${name}.vue`]) return resolvePageComponent(`./Pages/default/${name}.vue`, pages);
        return resolvePageComponent(`./Pages/${name}.vue`, pages);
    },
    setup({ el, App, props, plugin }) {
        // Website-builder preview mode: lets variants subscribe to live
        // postMessage updates from the parent admin form instead of
        // reading frozen Inertia props.
        const initialProps = props.initialPage?.props || {};
        if (initialProps.previewMode === true) {
            window.__previewMode = true;
            // Seed __pvFallbacks from the server-built copyPack so the
            // inline editor can map clicked text to a slot key on pages
            // that don't run text through useCopy/useDomainCopy (e.g.
            // LocationPicker, BrandHome — they read props.copyPack
            // directly and never call t()/ts()).
            if (initialProps.copyPack && typeof initialProps.copyPack === 'object') {
                if (!window.__pvFallbacks) window.__pvFallbacks = {};
                for (const [k, v] of Object.entries(initialProps.copyPack)) {
                    if (typeof v === 'string' && v.length > 0) {
                        window.__pvFallbacks[k] = v;
                    }
                }
            }
            // Free-text overrides: applied by a post-mount DOM walker so
            // *any* visible text (nav labels, hardcoded UI strings) can be
            // edited even when it isn't part of a known copy slot.
            window.__pvTextOverrides = (initialProps.textOverrides && typeof initialProps.textOverrides === 'object')
                ? { ...initialProps.textOverrides }
                : {};
        }

        // Inline-edit overlay (?edit=1 on the preview URL). Loaded lazily —
        // ships zero overhead when the flag isn't set.
        if (initialProps.previewMode === true && initialProps.previewEditMode === true) {
            import('./lib/inlineEditor').then(({ installInlineEditor }) => {
                // Wait a tick for the variant tree (Vue + nested React) to
                // settle so the DOM walker sees the final text nodes.
                setTimeout(() => {
                    installInlineEditor({
                        copyPack: initialProps.copyPack || {},
                        manifest: initialProps.previewVariantManifest || null,
                        service: initialProps.service || 'pv',
                    });
                }, 250);
            });
        }

        const vueApp = createApp({ render: () => h(App, props) })
            .use(plugin)
            .directive('glossary', vGlossary)
            .mount(el);

        document.body.dataset.appReady = 'true';

        // Apply free-text + image overrides to the rendered DOM. Runs in
        // both preview mode (where the admin builder also pushes live
        // updates) and on the public homepage so saved overrides render
        // in production.
        const propsTextOverrides = props.initialPage?.props?.textOverrides;
        if (propsTextOverrides && typeof propsTextOverrides === 'object') {
            window.__pvTextOverrides = { ...(window.__pvTextOverrides || {}), ...propsTextOverrides };
        }
        const propsImageOverrides = props.initialPage?.props?.imageOverrides;
        if (propsImageOverrides && typeof propsImageOverrides === 'object') {
            window.__pvImageOverrides = { ...(window.__pvImageOverrides || {}), ...propsImageOverrides };
        }
        const applyTextOverrides = () => {
            const map = window.__pvTextOverrides || {};
            if (!map || Object.keys(map).length === 0) return;
            const walker = document.createTreeWalker(
                document.body, NodeFilter.SHOW_TEXT,
                {
                    acceptNode(node) {
                        if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
                        const parent = node.parentElement;
                        if (!parent) return NodeFilter.FILTER_REJECT;
                        const tag = parent.tagName;
                        if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
                        return NodeFilter.FILTER_ACCEPT;
                    },
                },
            );
            let node;
            while ((node = walker.nextNode())) {
                const trimmed = node.nodeValue.trim();
                if (!trimmed) continue;
                const replacement = map[trimmed] ?? map[node.nodeValue];
                if (typeof replacement === 'string' && replacement !== trimmed) {
                    node.nodeValue = node.nodeValue.replace(trimmed, replacement);
                }
            }
        };
        // Image src replacement: every <img> whose original src matches
        // an entry in __pvImageOverrides gets rewritten. Also stamp the
        // original src on data-orig-src so the inline editor can find
        // the saved-from key after the swap.
        const applyImageOverrides = () => {
            const map = window.__pvImageOverrides || {};
            if (!map || Object.keys(map).length === 0) return;
            const imgs = document.querySelectorAll('img');
            imgs.forEach((img) => {
                const orig = img.dataset.origSrc || img.getAttribute('src');
                if (!orig) return;
                const replacement = map[orig];
                if (typeof replacement === 'string' && replacement && img.src !== replacement) {
                    if (!img.dataset.origSrc) img.dataset.origSrc = orig;
                    img.src = replacement;
                }
            });
        };
        // Initial pass once the SPA has painted.
        requestAnimationFrame(() => setTimeout(() => {
            applyTextOverrides();
            applyImageOverrides();
        }, 80));
        // Preview mode: replay walkers on every pv-preview message so
        // live edits from the admin builder show without a reload.
        if (window.__previewMode) {
            window.addEventListener('message', (e) => {
                if (e?.data?.type !== 'pv-preview') return;
                if (e.data.textOverrides && typeof e.data.textOverrides === 'object') {
                    window.__pvTextOverrides = { ...(window.__pvTextOverrides || {}), ...e.data.textOverrides };
                }
                if (e.data.imageOverrides && typeof e.data.imageOverrides === 'object') {
                    window.__pvImageOverrides = { ...(window.__pvImageOverrides || {}), ...e.data.imageOverrides };
                }
                setTimeout(() => {
                    applyTextOverrides();
                    applyImageOverrides();
                }, 80);
            });
        }

        // Always reset scroll to top after Vue mounts — SEO fallback IDs cause browser auto-scroll
        // and the variant chrome (React, mounted via veaury after Vue hydrates) pushes body
        // content down post-load. The 0/120/400ms cascade compensates for the late-mount
        // layout shift so users always land at the actual top.
        const forceScrollTop = () => {
            document.documentElement.style.scrollBehavior = 'auto';
            window.scrollTo(0, 0);
            requestAnimationFrame(() => {
                document.documentElement.style.scrollBehavior = '';
            });
        };
        forceScrollTop();
        // Strip hash to prevent browser re-scrolling on repaint
        if (window.location.hash) {
            history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        setTimeout(forceScrollTop, 120);
        setTimeout(forceScrollTop, 400);

        // Preview mode: carry preview_domain param across all navigations
        router.on('before', (event) => {
            const pd = new URLSearchParams(window.location.search).get('preview_domain');
            if (pd && event.detail?.visit?.url) {
                const url = event.detail.visit.url;
                if (!url.searchParams.has('preview_domain')) {
                    url.searchParams.set('preview_domain', pd);
                }
            }
        });

        // Reset scroll to top on every Inertia navigation. Same cascade
        // as the initial mount — variant chrome hydrates async and
        // shifts layout under us, so we re-scroll a few frames later.
        router.on('navigate', () => {
            forceScrollTop();
            setTimeout(forceScrollTop, 120);
            setTimeout(forceScrollTop, 400);
        });

        // IntersectionObserver: wire up .section-fade-in -> .visible class
        const initFadeInObserver = () => {
            if (!('IntersectionObserver' in window)) {
                document.querySelectorAll('.section-fade-in').forEach(el => el.classList.add('visible'));
                return;
            }
            const observer = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                        observer.unobserve(entry.target);
                    }
                }
            }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
            document.querySelectorAll('.section-fade-in').forEach(el => observer.observe(el));
            // Re-observe on Inertia navigations
            router.on('navigate', () => {
                setTimeout(() => {
                    document.querySelectorAll('.section-fade-in:not(.visible)').forEach(el => observer.observe(el));
                }, 100);
            });
        };
        initFadeInObserver();

        const initialPageProps = props.initialPage?.props || {};

        // Replay flag is set EAGERLY (before the idle-callback) so
        // sessionReplay.js can start recording within the first ~50ms
        // of page interactive. Bouncing visitors who leave in 2-3s were
        // exiting before the idle callback fired and capture started -
        // they showed up in tracking_events but had zero replay rows.
        if (initialPageProps.domain?.session_replay_enabled) {
            window.__sessionReplayEnabled = true;
            const samplePct = Number(initialPageProps.domain?.session_replay_sample_pct ?? 100);
            if (Math.random() * 100 < samplePct) {
                // Fire immediately, do not wait for requestIdleCallback.
                import('./lib/sessionReplay').then(m => m.initSessionReplay()).catch(() => {});
            }
        }

        const bootNonCriticalFeatures = () => {
            let trackingStarted = false;
            const startTracking = () => {
                if (trackingStarted) return;
                trackingStarted = true;

                import('./lib/tracking').then(({ initTracking, trackEvent }) => {
                    initTracking(initialPageProps.domain || {}, initialPageProps);

                    router.on('navigate', (event) => {
                        const page = event?.detail?.page;

                        trackEvent('page_view_client', {
                            event_category: 'navigation',
                            page_path: page?.url || `${window.location.pathname}${window.location.search}`,
                            page_url: `${window.location.origin}${page?.url || window.location.pathname}`,
                            service_type: page?.props?.service,
                            city_slug: page?.props?.location?.slug || page?.props?.city?.slug,
                            location_id: page?.props?.location?.id || page?.props?.city?.id,
                            location_slug: page?.props?.location?.slug || page?.props?.city?.slug,
                            location_name: page?.props?.location?.name || page?.props?.city?.name,
                            design_variant_id: page?.props?.designVariant?.id,
                            design_variant_family: page?.props?.designVariant?.family,
                            design_variant_name: page?.props?.designVariant?.name,
                            funnel_variant_id: page?.props?.funnelVariant?.id,
                            funnel_variant_family: page?.props?.funnelVariant?.family,
                            funnel_variant_name: page?.props?.funnelVariant?.name,
                            metadata: {
                                source: 'inertia_navigate',
                                component: page?.component,
                            },
                        });
                    });
                });
            };

            window.addEventListener('sw:analytics-consent', startTracking);
            import('./lib/cookieConsent').then(m => m.initCookieConsent(initialPageProps.domain || {}));
        };

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(bootNonCriticalFeatures, { timeout: 1500 });
        } else {
            window.setTimeout(bootNonCriticalFeatures, 200);
        }

        return vueApp;
    },
    progress: {
        color: '#2563EB',
    },
});

router.on('invalid', (event) => {
    if (event.detail.response?.status === 419) {
        event.preventDefault();
        window.location.reload();
    }
});
